import React from 'react';
import Button from '../../components/Button/Button';
import Modal, { ModalActions, ModalTitle } from '../../components/Modal/Modal';
import { useToast } from '../../components/Toast/ToastContext';
import styles from './Account.module.scss';

interface ApiKeyRevealModalProps {
  /** The freshly issued key. The modal is open while this is set. */
  plaintextKey: string | null;
  /** Called from "I've copied it" — the only way out, so the key can't be dismissed unseen. */
  onDone: () => void;
}

function noop() {
  // Escape, the backdrop and the close X are all disabled while the key is shown.
}

/**
 * One-time reveal of a newly issued API key. The backend stores only a hash, so once this closes the
 * key can't be shown again — the only recourse is to regenerate.
 */
export default function ApiKeyRevealModal({ plaintextKey, onDone }: ApiKeyRevealModalProps) {
  const { showToast } = useToast();

  function handleCopy() {
    if (!plaintextKey) {
      return;
    }
    navigator.clipboard.writeText(plaintextKey).then(
      () => showToast('API key copied!'),
      () => showToast('Failed to copy'),
    );
  }

  return (
    <Modal open={plaintextKey !== null} onClose={noop} showCloseX={false} dismissOnBackdropClick={false}>
      <ModalTitle>Your API key</ModalTitle>
      <div className={styles.apiKeyWarning} role="alert">
        Copy this key now. For security it is only shown once and can't be retrieved again. If it's lost, regenerate a
        new key from this page.
      </div>
      <div className={styles.apiKeyReveal}>
        <code className={styles.apiKeyPlaintext} data-testid="api-key-plaintext">
          {plaintextKey}
        </code>
        <Button variant="greenOutline" size="sm" onClick={handleCopy}>
          Copy
        </Button>
      </div>
      <ModalActions>
        <Button variant="green" onClick={onDone}>
          I've copied it
        </Button>
      </ModalActions>
    </Modal>
  );
}
